import type { Task } from '../types';
import { escalatedPriority, priorityRank } from './priority';

export type SortMode = 'smart' | 'due' | 'priority' | 'age';

function isOverdue(task: Task, now = new Date()): boolean {
  if (!task.dueDate || task.status === 'completed') return false;
  return new Date(task.dueDate).getTime() < now.getTime();
}

function smartCompare(a: Task, b: Task, now: Date): number {
  if (a.status === 'completed' && b.status !== 'completed') return 1;
  if (b.status === 'completed' && a.status !== 'completed') return -1;

  if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;

  const aOver = isOverdue(a, now);
  const bOver = isOverdue(b, now);
  if (aOver !== bOver) return aOver ? -1 : 1;

  const aEff = escalatedPriority(a.priority, a.dueDate, now);
  const bEff = escalatedPriority(b.priority, b.dueDate, now);
  const pDiff = priorityRank(aEff) - priorityRank(bEff);
  if (pDiff !== 0) return pDiff;

  const aHasDue = !!a.dueDate;
  const bHasDue = !!b.dueDate;
  if (aHasDue !== bHasDue) return aHasDue ? -1 : 1;
  if (aHasDue && bHasDue) {
    const d = new Date(a.dueDate!).getTime() - new Date(b.dueDate!).getTime();
    if (d !== 0) return d;
  }

  return a.title.localeCompare(b.title);
}

/** Completed always sinks to the bottom regardless of mode. Pinned always floats
 *  to the top inside its status group. */
function statusAndPinPrefix(a: Task, b: Task): number {
  if (a.status === 'completed' && b.status !== 'completed') return 1;
  if (b.status === 'completed' && a.status !== 'completed') return -1;
  if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
  return 0;
}

function dueCompare(a: Task, b: Task): number {
  const pre = statusAndPinPrefix(a, b);
  if (pre !== 0) return pre;
  const aHas = !!a.dueDate;
  const bHas = !!b.dueDate;
  if (aHas !== bHas) return aHas ? -1 : 1;
  if (aHas && bHas) {
    return new Date(a.dueDate!).getTime() - new Date(b.dueDate!).getTime();
  }
  return a.title.localeCompare(b.title);
}

function priorityCompare(a: Task, b: Task, now: Date): number {
  const pre = statusAndPinPrefix(a, b);
  if (pre !== 0) return pre;
  const aEff = escalatedPriority(a.priority, a.dueDate, now);
  const bEff = escalatedPriority(b.priority, b.dueDate, now);
  const pDiff = priorityRank(aEff) - priorityRank(bEff);
  if (pDiff !== 0) return pDiff;
  const aHas = !!a.dueDate;
  const bHas = !!b.dueDate;
  if (aHas && bHas) {
    return new Date(a.dueDate!).getTime() - new Date(b.dueDate!).getTime();
  }
  return a.title.localeCompare(b.title);
}

/** Age: newest-first (most recently created at top). */
function ageCompare(a: Task, b: Task): number {
  const pre = statusAndPinPrefix(a, b);
  if (pre !== 0) return pre;
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}

export function sortTasks(tasks: Task[], mode: SortMode = 'smart', now = new Date()): Task[] {
  const cmp =
    mode === 'due'
      ? (a: Task, b: Task) => dueCompare(a, b)
      : mode === 'priority'
        ? (a: Task, b: Task) => priorityCompare(a, b, now)
        : mode === 'age'
          ? (a: Task, b: Task) => ageCompare(a, b)
          : (a: Task, b: Task) => smartCompare(a, b, now);
  return [...tasks].sort(cmp);
}

export const SORT_LABELS: Record<SortMode, string> = {
  smart: 'Smart',
  due: 'Due date',
  priority: 'Priority',
  age: 'Newest',
};
